import { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { faqs } from '@/mocks/services';

gsap.registerPlugin(ScrollTrigger);

interface FaqItem { q: string; a: string }

const prefersReduced = () => typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Gentle, symmetric curve: soft start, long soft landing.
const EASE = 'power3.inOut';

function FaqRow({ item, index, isOpen, onToggle }: {
  item: FaqItem; index: number; isOpen: boolean; onToggle: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  const mounted = useRef(false);
  const panelId = `faq-panel-${index}`;
  const btnId = `faq-btn-${index}`;

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const inner = innerRef.current;
    const bar = barRef.current;
    if (!panel || !inner || !bar) return undefined;

    if (!mounted.current || prefersReduced()) {
      mounted.current = true;
      gsap.set(panel, { height: isOpen ? 'auto' : 0 });
      gsap.set(inner, { opacity: isOpen ? 1 : 0, y: 0 });
      gsap.set(bar, { scaleY: isOpen ? 0 : 1, rotate: isOpen ? 90 : 0 });
      return undefined;
    }

    // Start from the current rendered height so interrupted toggles never jump.
    const from = panel.getBoundingClientRect().height;
    const to = isOpen ? inner.offsetHeight : 0;
    const tl = gsap.timeline({
      onComplete: () => {
        if (isOpen) gsap.set(panel, { height: 'auto' });
        ScrollTrigger.refresh();
      },
    });

    tl.fromTo(panel, { height: from }, {
      height: to,
      duration: isOpen ? 0.65 : 0.55,
      ease: EASE,
    }, 0);

    if (isOpen) {
      tl.fromTo(inner, { opacity: 0, y: 10 }, {
        opacity: 1, y: 0, duration: 0.5, ease: 'power2.out',
      }, 0.18);
    } else {
      tl.to(inner, { opacity: 0, y: 6, duration: 0.28, ease: 'power1.out' }, 0);
    }

    tl.to(bar, {
      scaleY: isOpen ? 0 : 1,
      rotate: isOpen ? 90 : 0,
      duration: 0.5,
      ease: EASE,
    }, 0);

    return () => { tl.kill(); };
  }, [isOpen]);

  return (
    <div className={`mw-trace border-t border-background-200 ${isOpen ? 'is-active' : ''}`}>
      <button
        id={btnId}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex w-full cursor-pointer items-start gap-5 md:gap-8 py-6 text-left"
      >
        <span className={`pt-1 text-xs transition-colors duration-500 ${isOpen ? 'text-primary-600' : 'text-foreground-600'}`}>{`0${index + 1}`}</span>
        <span className="mw-trace-text flex-1 text-base md:text-lg font-normal tracking-tight text-foreground-950">{item.q}</span>
        <span className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors duration-500 ease-in-out ${isOpen ? 'border-primary-500 bg-primary-500 text-white' : 'border-background-200 text-foreground-950'}`}>
          <span className="absolute h-[1.5px] w-3 rounded-full bg-current" />
          <span ref={barRef} className="absolute h-3 w-[1.5px] rounded-full bg-current" />
        </span>
      </button>
      <div
        id={panelId}
        ref={panelRef}
        role="region"
        aria-labelledby={btnId}
        aria-hidden={!isOpen}
        className="overflow-hidden"
      >
        <div ref={innerRef} className="flow-root will-change-transform">
          <p className="-mt-2 pb-6 pl-9 md:pl-12 pr-12 max-w-2xl text-sm md:text-[15px] leading-relaxed text-foreground-600">{item.a}</p>
        </div>
      </div>
    </div>
  );
}

export default function FaqList({ items = faqs }: { items?: FaqItem[] }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="border-b border-background-200">
      {items.map((f, i) => (
        <FaqRow
          key={f.q}
          item={f}
          index={i}
          isOpen={open === i}
          onToggle={() => setOpen(open === i ? null : i)}
        />
      ))}
    </div>
  );
}